import { makeStyles } from '@mui/styles';
import Card from 'apollo-react/components/Card';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { DownloadCell } from './DashboardTabTable/DownloadCell';
import { compareStrings, createCustomCell } from '../../../components/SavedSearchesTable/utils';
import withTranslation from '../../../components/withTranslation';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getOfflineSearchResults } from '../../../form-designer/selectors/common.selectors';
import { getIsRtlLanguage } from '../../../selectors/AuthSelectors';
import NavigateLinkCell from '../../FindPage/NavigateLinkCell';

const useStyles = makeStyles({
  tableContainer: {
    '& [class*="MuiPaper"]': {
      overflowX: 'auto',
      overflowY: 'hidden',
    },
    '& [class*="MuiTable-root"]': {
      minHeight: 486.856,
    },
  },
  card: {
    height: '100%',
    paddingBottom: 0,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
    marginLeft: 16,
    marginTop: 16,
    marginRight: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.04,
  },
  subtitle: {
    color: '#595959',
    fontSize: 14,
    lineHeight: 1.71,
  },
});

const extraColumn = {
  header: '',
  accessor: 'OFFLINE_MODE',
  dataType: 'System.String',
  fixedWidth: true,
  // eslint-disable-next-line react/display-name
  customCell: ({ row }) => {
    return <DownloadCell row={row} />;
  },
};

const appendIfNeeded = (extraColumn, dataColumns) =>
  dataColumns.length ? [...dataColumns, extraColumn] : [];

const DashboardOfflinePart = ({ translateString }) => {
  const classes = useStyles();
  const translations = useTranslation();
  const offlineResults = useSelector(getOfflineSearchResults) ?? {};
  const isRtlLanguage = useSelector(getIsRtlLanguage);

  const rows = offlineResults.rows ?? [];
  const columns = offlineResults.columns ?? [];
  const tableRowHeadRef = useRef(null);

  const dataColumns = columns.map((column, index) => ({
    ...column,
    sortFunction: compareStrings,
    customCell:
      index !== 0
        ? createCustomCell(column, translations)
        : NavigateLinkCell.fromNavigationColumn(column),
  }));

  useEffect(() => {
    if (isRtlLanguage) {
      const applyAttributeTimer = setTimeout(() => {
        return tableRowHeadRef.current?.columnHeaderRootRef.current.setAttribute('dir', 'ltr');
      }, 1000);
      return () => clearTimeout(applyAttributeTimer);
    }
  }, [isRtlLanguage]);

  return (
    <Card className={classes.card}>
      <Grid item className={classes.titleContainer}>
        <Grid item>
          <Typography variant="title1" className={classes.title}>
            {translateString('My Offline Tasks')}
          </Typography>
          <Typography className={classes.subtitle}>
            {translateString('List of local copy tasks')}
          </Typography>
        </Grid>
      </Grid>

      <div className={classes.tableContainer}>
        <Table
          rows={rows}
          columns={appendIfNeeded(extraColumn, dataColumns)}
          size={rows.length}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          tablePaginationProps={{
            labelRowsPerPage: translateString('Rows'),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${translateString('of')} ${count}`,
            pageText: translateString('Page'),
            ofText: translateString('of'),
          }}
          ref={tableRowHeadRef}
          sortTooltipText={translateString('Sort')}
        />
      </div>
    </Card>
  );
};

export default withTranslation(DashboardOfflinePart);
